import { ComponentProps, createContext, useContext, useState } from "react";
import { ApiListTenant, ApiTenant } from "src/api/types";
import Button, { ButtonProps } from "src/components/Button";
import { useT } from "src/components/hoc/withTranslation";
import Logo from "src/components/Logo";
import useTenants from "src/hooks/api/services/tenants/useTenants";
import useAuth from "src/hooks/selectors/useAuth";
import useAlert from "src/hooks/useAlert";
import { cn } from "src/lib/utils";

type TenantContextType = {
	tenant: ApiListTenant;
};

const TenantContext = createContext<TenantContextType>({} as TenantContextType);

interface TenantProps {
	id: ApiTenant["slug"];
	children: React.ReactNode;
}
export default function Tenant({ id, children }: TenantProps) {
	const { tenants } = useTenants();

	const tenant = tenants.find((tenant) => tenant.slug === id);

	if (!tenant) {
		return null;
	}

	return (
		<TenantContext.Provider value={{ tenant }}>
			{children}
		</TenantContext.Provider>
	);
}

export function TenantName({
	className,
	...rest
}: Omit<ComponentProps<"span">, "children">) {
	const { tenant } = useContext(TenantContext);
	return (
		<span className={cn(className)} {...rest}>
			{tenant.name}
		</span>
	);
}

interface TenantDescriptionProps
	extends Omit<ComponentProps<"span">, "children"> {
	variant?: "short" | "long";
}

export function TenantDescription({
	className,
	variant = "short",
	...rest
}: TenantDescriptionProps) {
	const { tenant } = useContext(TenantContext);
	const auth = useAuth();
	const t = useT({
		nl: {
			short: {
				getcrewdone: {
					student: "Werk op de dikste festivals",
					company: "Schakel jongeren in voor jouw event",
				},
				getjobsdone: {
					student: "Doe opdrachten die passen bij je skills",
					company:
						"Schakel (Young) Professionals in voor jouw bedrijf",
				},
				getrecruitmentdone: {
					student: "Vind jouw volgende job",
					company: "Vind (Young) Professionals voor al je vacatures",
				},
				getstagedone: {
					student: "Vind jouw volgende stage",
					company: "Vind (Young) Professionals voor je stageplekken",
				},
			},
			long: {
				getcrewdone: {
					student:
						"Beleef je favoriete festival samen met je vrienden, terwijl je werkt! Kies uit diverse klussen zoals biertappen, opbouwen of ticketscanning, en werk voor de opdrachtgever van jouw keuze.",
					company:
						"Mensen nodig voor je evenement? Bij GetCrewDone schakel je direct jongeren in voor jouw event. Jij kiest de opdrachtnemers, wij regelen de overeenkomsten en betalingen.",
				},
				getjobsdone: {
					student:
						"Wil je wat bijverdienen en tegelijk aan je skills werken? Bij GetJobsDone kan je je aanmelden op opdrachten die passen bij jouw skills. Voltooi de opdracht en krijg online uitbetaald.",
					company:
						"Iemand nodig die voordelig een taak voor je oplost? Bij GetJobsDone schakel je (Young) Professionals in die voor een vast bedrag een taak voor je uit handen nemen.",
				},
				getrecruitmentdone: {
					student:
						"Ben je op zoek naar een nieuwe baan? GetRecruitmentDone helpt je gratis aan de beste job, perfect afgestemd op jouw vaardigheden.",
					company:
						"Vacature niet ingevuld? Plaats hem op GetRecruitmentDone en wij vinden de perfecte kandidaat in onze database op basis van matchende vaardigheden.",
				},
				getstagedone: {
					student:
						"Opzoek naar een stage? Op GetStageDone vind je stageplekken die bij jouw skills passen.",
					company:
						"Heeft jouw bedrijf een openstaande stageplek? Op GetStageDone vind je snel de juiste stagiair.",
				},
			},
		},
		en: {
			short: {
				getcrewdone: {
					student: "Work at the coolest festivals",
					company: "Hire young people for your event",
				},
				getjobsdone: {
					student: "Do assignments that match your skills",
					company: "Hire (Young) Professionals for your company",
				},
				getrecruitmentdone: {
					student: "Find your next job",
					company:
						"Find (Young) Professionals for all your vacancies",
				},
				getstagedone: {
					student: "Find your next internship",
					company: "Find (Young) Professionals for your internships",
				},
			},
			long: {
				getcrewdone: {
					student:
						"Experience your favorite festival with your friends while you work! Choose from various jobs such as bartending, setting up, or ticket scanning, and work for the client of your choice.",
					company:
						"Need people for your event? With GetCrewDone, you can directly hire young people for your event. You choose the contractors, we take care of the agreements and payments.",
				},
				getjobsdone: {
					student:
						"Want to earn some extra money and work on your skills at the same time? At GetJobsDone, you can sign up for assignments that match your skills. Complete the assignment and get paid online.",
					company:
						"Need someone to solve a task for you affordably? At GetJobsDone, you can hire (Young) Professionals who will take care of a task for you for a fixed price.",
				},
				getrecruitmentdone: {
					student:
						"Are you looking for a new job? GetRecruitmentDone helps you find the best job for free, perfectly tailored to your skills.",
					company:
						"Vacancy not filled? Place it on GetRecruitmentDone and we will find the perfect candidate in our database based on matching skills.",
				},
				getstagedone: {
					student:
						"Looking for an internship? At GetStageDone you will find internships that match your skills.",
					company:
						"Does your company have an open internship position? At GetStageDone you can quickly find the right intern.",
				},
			},
		},
	});

	return (
		<span
			className={cn(className)}
			{...rest}
			dangerouslySetInnerHTML={{
				__html: t(
					`${variant}.${tenant.slug}.${auth?.type || "company"}`
				),
			}}
		></span>
	);
}

export function TenantLogo({
	...rest
}: Omit<ComponentProps<typeof Logo>, "slug">) {
	const { tenant } = useContext(TenantContext);
	return <Logo {...rest} slug={tenant.slug} />;
}

interface JoinTenantButtonProps extends Omit<ButtonProps, "children"> {
	textVariant?: "minimal" | "full";
}

export const JoinTenantButton = ({
	textVariant = "full",
	...props
}: JoinTenantButtonProps) => {
	const { tenant } = useContext(TenantContext);
	const t = useT({
		nl: {
			join: "Join",
			joined: "Al ingeschakeld",
			confirm: {
				title: "Wil je je account inschakelen?",
				text: "Wanneer je op bevestigen klikt, wordt je account bij {{name}} ingeschakeld en word je doorverwezen.",
			},
		},
		en: {
			join: "Join",
			joined: "Already enabled",
			confirm: {
				title: "Do you want to activate your account?",
				text: "When you click confirm, your account will be activated and you will be redirected.",
			},
		},
	});
	const [isLoading, setIsLoading] = useState(false);
	const [sendAlert] = useAlert();
	const { actions } = useTenants();

	const onClick = () => {
		if (tenant.has_access) {
			return;
		}
		sendAlert({
			type: "confirm",
			title: t("confirm.title"),
			text: t("confirm.text", {
				name: tenant.name,
			}),
			onConfirm: async () => {
				setIsLoading(true);
				try {
					const data = await actions.join(
						tenant,
						"FROM_OUR_PRODUCTS"
					);
					window.location = data.redirect_url;
				} finally {
					setIsLoading(false);
				}
			},
		});
	};
	return (
		<div data-theme={tenant.slug}>
			<Button
				type="primary"
				xsmall
				{...props}
				disabled={tenant.has_access}
				{...{ onClick }}
				loading={isLoading}
			>
				{textVariant === "minimal" && (
					<>
						{tenant.has_access ? (
							<i className="far fa-check"></i>
						) : (
							t("join")
						)}
					</>
				)}

				{textVariant === "full" && (
					<>{tenant.has_access ? t("joined") : t("join")}</>
				)}
			</Button>
		</div>
	);
};
